import { useId, type ReactNode } from 'react';

export interface FormFieldProps {
  label: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

/**
 * Accessible label + control + hint/error wiring (WCAG 2.2 AA).
 * `children` is a render prop so any control can be wired correctly.
 */
export function FormField({ label, children, hint, error, required, className }: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={['u-field', className].filter(Boolean).join(' ')}>
      <label className="u-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="u-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span className="u-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="u-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
